import React, {Fragment, useContext, useEffect, useMemo, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import DropdownListForm, {
    DropdownListFormNotSelectedStringId,
    DropdownListItem,
} from "../../components/forms/dropdownListForm";
import Form from "../../components/forms/form";
import ButtonForm from "../../components/forms/buttonForm";
import {
    dataForAgeType,
    dataForChronicCondition,
    dataForGender,
    dataForPatientPanel,
    dataForPlanType,
    dataForPreferredLanguage,
    getDefaultNewGroupSetting,
    LastDOS,
} from "./dataForNewGroup";
import CalendarPicker from "../../components/datePicker/calendarPicker";
import moment from "moment";
import {ActionType, PresentationStyle, TimeFormat} from "../../util/enum";
import {
    getAppointmentTypes,
    getCodeTemplate,
    getPatientByFilter,
    getPatientInsurance,
    savePatientGroup,
} from "./groupService";
import Spinner from "../../components/spinner/spinner";
import {ProviderContext} from "../providerContext";
import MultiSelectForm, {MultiSelectFormItem} from "../../components/forms/multiSelectForm";
import Notification, {NotificationType} from "../../components/modal/notification";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import CustomModal from "../../components/modal/customModal";
import CodeSelector, {CodeItem, CodeSelectorType, CodeTemplateItem} from "./codeSelector";
import {Listbox, Transition} from "@headlessui/react";
import {PromptContext} from "../promptContext";
import {SharedContext, SharedContextActionType} from "../sharedContextProvider";
import PatientPreviewList, {SelectPatient} from "./patientPreviewList";
import Alert from "../../components/modal/alert";
import {GraylogContext} from "../graylogContext";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";

export interface NewGroupSetting {
    id: string,
    patientName: string,
    fromAge: string,
    toAge: string,
    ageType: AgeType,
    gender: string,
    preferredContact: string,
    preferredLanguage: string,
    lastDOS: string | LastDOS,
    dosFrom: Date | null,
    dosTo: Date | null,
    appointmentType: string,
    chronicCondition: Array<string>,
    planType: string,
    insurance: string,
    insuranceName: string,
    icd: Array<CodeItem>,
    excludeIcd: Array<CodeItem>,
    cpt: Array<CodeItem>,
    excludeCpt: Array<CodeItem>,
    icdFrom: Date | null,
    icdTo: Date | null,
    cptFrom: Date | null,
    cptTo: Date | null,
    patientPanel: string,
    groupName: string
}

export interface GroupEmployee {
    clinicID: number,
    employeeID: number,
    mEmployeeID: number,
    employeeFirstName: string,
    employeeMidName: string,
    employeeLastName: string
}

enum CalendarPickerType {
    none,
    icdFrom,
    icdTo,
    cptFrom,
    cptTo,
    dosFrom,
    dosTo,
}

export enum AgeType {
    byYear = "Y",
    byMonth = "M",
}

interface ILocation {
    setting: NewGroupSetting | null,
    matchingCount: number | undefined,
}

interface IProps {
    presentationStyle?: PresentationStyle,
    onDismiss?: () => void
}

export const MaxMatchingPatientCount = 6000

function NewGroup(props: IProps) {
    const {presentationStyle = PresentationStyle.push, onDismiss} = props
    const {state} = useLocation<ILocation>()
    const defaultSetting = state?.setting ? state?.setting : getDefaultNewGroupSetting()
    const [groupName, setGroupName] = useState<string>(defaultSetting.groupName)
    const [newGroupSetting, setNewGroupSetting] = useState<NewGroupSetting>(defaultSetting)
    const [appointmentTypes, setAppointmentTypes] = useState<Array<DropdownListItem>>([{name: "-----", id: DropdownListFormNotSelectedStringId}])
    const [insuranceList, setInsuranceList] = useState<Array<DropdownListItem>>([{name: "-----", id: DropdownListFormNotSelectedStringId}])

    const [isShowAdvanced, setIsAdvanced] = useState<boolean>(!!state?.setting)
    const [isShowSpinner, setIsShowSpinner] = useState(false)
    const [showCalendarPicker, setShowCalendar] = useState<boolean>(false)
    const [calendarPickerType, setCalendarPickerType] = useState<CalendarPickerType>(CalendarPickerType.none)
    const [matchingCount, setMatchingCount] = useState<number>(state?.matchingCount || 0)
    const provider = useContext(ProviderContext)
    const [isValid, setIsValid] = useState<boolean>(true)
    const [fromAgeInvalidMsg, setFromAgeInvalidMsg] = useState<string>("")
    const [toAgeInvalidMsg, setToAgeInvalidMsg] = useState<string>("")
    const [showNotification, setShowNotification] = useState<boolean>(false)
    const [showPatientPreviewModal, setShowPatientPreviewModal] = useState<boolean>(false)
    const [showUnSavedChangeAlert, setShowUnSavedChangeAlert] = useState<boolean>(false)
    const [showMatchingCountExceedAlert, setShowMatchingCountExceedAlert] = useState<boolean>(false)
    const [excludePatients, setExcludePatients] = useState<Array<SelectPatient>>([])

    const [isIncludeIcd, setIsIncludeIcd] = useState<boolean>(!defaultSetting.excludeIcd.length)
    const [isIncludeCpt, setIsIncludeCpt] = useState<boolean>(!defaultSetting.excludeCpt.length)

    const [codeType, setCodeType] = useState<CodeSelectorType>(CodeSelectorType.none)
    const [showCodeSelector, setShowCodeSelector] = useState<boolean>(false)
    const [dataForIcdCodeTemplate, setDataForIcdCodeTemplate] = useState<Array<CodeTemplateItem>>([])
    const [dataForCptCodeTemplate, setDataForCptCodeTemplate] = useState<Array<CodeTemplateItem>>([])
    const {sharedContextState, dispatchSharedContext} = useContext(SharedContext)

    const logEmployee = useContext(GraylogContext)

    const history = useHistory()
    const {showError, showInfo} = useContext(PromptContext)

    const { search } = useLocation<ILocation>()
    const query = (new URLSearchParams(search))
    let actionType: ActionType = ActionType.new
    if (query.get("actionType")) {
        actionType = query.get("actionType") as ActionType
    }

    // get relative icd & cpt id.
    useEffect(() => {
        const clinicID = provider.clinicID
        getAppointmentTypes(clinicID, (types) => {
            setAppointmentTypes(appointmentTypes.concat(types))
        }, () => {
            //
        })

        getPatientInsurance(clinicID, (list) => {
            setInsuranceList(insuranceList.concat(list))
        }, () => {
            //
        })

        getCodeTemplate(provider.clinicID, CodeSelectorType.icd, (list) => {
            setDataForIcdCodeTemplate(list)
        }, () => {
            //
        })
        getCodeTemplate(provider.clinicID, CodeSelectorType.cpt, (list) => {
            setDataForCptCodeTemplate(list)
        }, () => {
            //
        })
    }, [])

    useEffect(() => {
        if (!onValidateFromAge(newGroupSetting.fromAge) || !onValidateToAge(newGroupSetting.toAge)) {
            return;
        }
        const excludePatientIds = excludePatients.map((pt) => pt.patientId)
        setIsShowSpinner(true)
        getPatientByFilter(newGroupSetting, 0, 0, 1, excludePatientIds, (data, matchingCount) => {
            setIsShowSpinner(false)
            setMatchingCount(matchingCount)
        }, (error) => {
            setIsShowSpinner(false)
            showError(error)
        })
    }, [newGroupSetting, excludePatients])

    useEffect(() => {
        if (newGroupSetting !== defaultSetting) {
            // reverse the data source
            const icdCodes = newGroupSetting.icd
            const excludeIcdCodes = newGroupSetting.excludeIcd
            setNewGroupSetting({
                ...newGroupSetting,
                icd: excludeIcdCodes,
                excludeIcd: icdCodes,
            })
        }
    }, [isIncludeIcd])

    useEffect(() => {
        if (newGroupSetting !== defaultSetting) {
            // reverse the data source
            const cptCodes = newGroupSetting.cpt
            const excludeCptCodes = newGroupSetting.excludeCpt
            setNewGroupSetting({
                ...newGroupSetting,
                cpt: excludeCptCodes,
                excludeCpt: cptCodes,
            })
        }
    }, [isIncludeCpt])

    useEffect(() => {
        if (codeType !== CodeSelectorType.none) {
            setShowCodeSelector(true)
        }
    }, [codeType])

    useEffect(() => {
        !sharedContextState.hasUnSavedChanges && dispatchSharedContext({type: SharedContextActionType.unsavedChange, state: true})
    }, [newGroupSetting])

    let largeTitle = "New Patient Group"
    if (actionType === ActionType.edit) {
        largeTitle = "Edit Patient Group"
    } else if (actionType === ActionType.view) {
        largeTitle = "View Patient Group"
    }

    const $routePathHeader = (<div className={"flex flex-row items-center"}>
        <button onClick={() => {
            if (sharedContextState.hasUnSavedChanges) {
                setShowUnSavedChangeAlert(true)
            } else {
                onClose()
            }
        }} type={"button"} className={"text-sm text-gray-2 border-b border-gray-400 hover:text-theme-blue hover:border-theme-blue"}>Patients</button>
        <p className={"text-sm text-gray-2 mx-2"}>{">"}</p>
        <p className={"text-sm text-gray-2"}>{largeTitle}</p>
    </div>)

    const $largeTitle = (<p className={"text-2xl text-gray-1 font-bold mt-2"}>{largeTitle}</p>)

    const $header = (
        presentationStyle === PresentationStyle.push ? (
            <div className={"w-full flex flex-row items-center justify-between"}>
                <div>
                    {$routePathHeader}
                    {$largeTitle}
                </div>
            </div>
        ) : null
    )

    const onValidateFromAge = (age: string) => {
        if (!age.length) {
            setFromAgeInvalidMsg( "")
            return true;
        }
        if (!checkNumber(age)) {
            setFromAgeInvalidMsg( "non-numeric format")
            return false;
        }

        const fromAge = parseInt(age)

        if (checkNumber(newGroupSetting.toAge)) {
            let toAge = parseInt(newGroupSetting.toAge)
            if (fromAge < toAge) {
                if (toAge <= (newGroupSetting.ageType === AgeType.byYear ? 130 : 1560)) {
                    setToAgeInvalidMsg("")
                }
            } else {
                setFromAgeInvalidMsg("The from age should be less than to age")
                return false;
            }
        }

        if (fromAge > (newGroupSetting.ageType === AgeType.byYear ? 130 : 1560)) {
            setFromAgeInvalidMsg(`Should be no greater than ${newGroupSetting.ageType === AgeType.byYear ? 130 : 1560}`)
            return false;
        }
        setFromAgeInvalidMsg("")
        return true;
    }

    const onValidateToAge = (age: string) => {
        if (!age.length) {
            setToAgeInvalidMsg( "")
            return true;
        }
        if (!checkNumber(age)) {
            setToAgeInvalidMsg( "non-numeric format")
            return false;
        }

        const toAge = parseInt(age)

        if (checkNumber(newGroupSetting.fromAge)) {
            let fromAge = parseInt(newGroupSetting.fromAge)
            if (fromAge < toAge) {
                if (fromAge <= (newGroupSetting.ageType === AgeType.byYear ? 130 : 1560)) {
                    setFromAgeInvalidMsg("")
                }
            } else {
                setToAgeInvalidMsg("The to age should be greater than from age")
                return false;
            }
        }

        if (toAge > (newGroupSetting.ageType === AgeType.byYear ? 130 : 1560)) {
            setToAgeInvalidMsg(`Should be no greater than ${newGroupSetting.ageType === AgeType.byYear ? 130 : 1560}`)
            return false
        }
        setToAgeInvalidMsg("")
        return true
    }

    const onChangeFromAge = (age: string) => {
        onValidateFromAge(age)

        setNewGroupSetting({
            ...newGroupSetting,
            fromAge: age,
        })
    }

    const onChangeToAge = (age: string) => {
        onValidateToAge(age)

        setNewGroupSetting({
            ...newGroupSetting,
            toAge: age,
        })
    }

    const onChangeGender = (id: string) => {
        setNewGroupSetting({
            ...newGroupSetting,
            gender: id,
        })
    }

    const onChangeAgeType = (id: string) => {
        setNewGroupSetting({
            ...newGroupSetting,
            ageType: id as AgeType,
        })
    }

    // stage 2
    // const onChangePreferredContact = (id: string) => {
    //     setNewGroupSetting({
    //         ...newGroupSetting,
    //         preferredContact: id,
    //     })
    // }

    const onChangePreferredLanguage = (id: string) => {
        setNewGroupSetting({
            ...newGroupSetting,
            preferredLanguage: id,
        })
    }

    const onChangeChronicCondition = (data: Array<MultiSelectFormItem>) => {
        const selectedIds = data.map(({id}) => id)
        if (newGroupSetting.chronicCondition.length !== selectedIds.length || newGroupSetting.chronicCondition.find((id) => !selectedIds.includes(id))) {
            setNewGroupSetting({
                ...newGroupSetting,
                chronicCondition: selectedIds,
            })
        }
    }

    const onChangePatientPanel = (id: string) => {
        setNewGroupSetting({
            ...newGroupSetting,
            patientPanel: id,
        })
    }

    const $onChangeDate = (date: Date) => {
        setShowCalendar(false)
        switch (calendarPickerType) {
        case CalendarPickerType.cptFrom:
            setNewGroupSetting({
                ...newGroupSetting,
                cptFrom: date,
            })
            break
        case CalendarPickerType.cptTo:
            setNewGroupSetting({
                ...newGroupSetting,
                cptTo: date,
            })
            break
        case CalendarPickerType.icdFrom:
            setNewGroupSetting({
                ...newGroupSetting,
                icdFrom: date,
            })
            break
        case CalendarPickerType.icdTo:
            setNewGroupSetting({
                ...newGroupSetting,
                icdTo: date,
            })
            break
        case CalendarPickerType.dosFrom:
            setNewGroupSetting({
                ...newGroupSetting,
                dosFrom: date,
            })
            break
        case CalendarPickerType.dosTo:
            setNewGroupSetting({
                ...newGroupSetting,
                dosTo: date,
            })
            break
        default:break
        }
    }

    // const onChangeAppointmentType = (id: string) => {
    //     setNewGroupSetting({
    //         ...newGroupSetting,
    //         appointmentType: id,
    //     })
    // }

    const onChangeInsurance = (id: string) => {
        const arr = id.split(":")
        if (arr.length > 1) {
            setNewGroupSetting({
                ...newGroupSetting,
                insurance: arr[0],
                insuranceName: arr[1],
            })
        }
    }

    const onChangePlanType = (id: string) => {
        setNewGroupSetting({
            ...newGroupSetting,
            planType: id,
        })
    }

    const onClickFromDOS = () => {
        setCalendarPickerType(CalendarPickerType.dosFrom)
        setShowCalendar(true)
    }

    const onClickToDOS = () => {
        setCalendarPickerType(CalendarPickerType.dosTo)
        setShowCalendar(true)
    }

    const onClickIcdFrom = () => {
        setCalendarPickerType(CalendarPickerType.icdFrom)
        setShowCalendar(true)
    }

    const onClickIcdTo = () => {
        setCalendarPickerType(CalendarPickerType.icdTo)
        setShowCalendar(true)
    }

    const onClickCptFrom = () => {
        setCalendarPickerType(CalendarPickerType.cptFrom)
        setShowCalendar(true)
    }

    const onClickCptTo = () => {
        setCalendarPickerType(CalendarPickerType.cptTo)
        setShowCalendar(true)
    }

    const formatDate = (date: Date | null) => {
        if (!date) {
            return ""
        }
        return moment(date).format(TimeFormat.MMDDYYYY)
    }
    const formatDOSFromDate = formatDate(newGroupSetting.dosFrom)
    const formatDOSToDate = formatDate(newGroupSetting.dosTo)
    const formatIcdFromDate = formatDate(newGroupSetting.icdFrom)
    const formatIcdToDate = formatDate(newGroupSetting.icdTo)
    const formatCptFromDate = formatDate(newGroupSetting.cptFrom)
    const formatCptToDate = formatDate(newGroupSetting.cptTo)

    const previewPatientList = () => {
        if (!matchingCount) {
            return
        }
        if (matchingCount <= MaxMatchingPatientCount) {
            setShowPatientPreviewModal(true)
        } else {
            showInfo(`Cannot preview more than ${MaxMatchingPatientCount} patients.`)
        }
    }

    const $iconCalendar = (
        <div>
            <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_673_429)">
                    <path d="M16.3214 2.25H14.3929V0.421875C14.3929 0.189844 14.1759 0 13.9107 0H12.3036C12.0384 0 11.8214 0.189844 11.8214 0.421875V2.25H6.67857V0.421875C6.67857 0.189844 6.46161 0 6.19643 0H4.58929C4.32411 0 4.10714 0.189844 4.10714 0.421875V2.25H2.17857C1.11384 2.25 0.25 3.00586 0.25 3.9375V16.3125C0.25 17.2441 1.11384 18 2.17857 18H16.3214C17.3862 18 18.25 17.2441 18.25 16.3125V3.9375C18.25 3.00586 17.3862 2.25 16.3214 2.25ZM16.0804 16.3125H2.41964C2.28705 16.3125 2.17857 16.2176 2.17857 16.1016V5.625H16.3214V16.1016C16.3214 16.2176 16.2129 16.3125 16.0804 16.3125Z" fill="black"/>
                </g>
                <defs>
                    <clipPath id="clip0_673_429">
                        <rect width="18" height="18" fill="white" transform="translate(0.25)"/>
                    </clipPath>
                </defs>
            </svg>
        </div>
    )

    const $codeSelectorButton = (type: CodeSelectorType, placeholder: string, value: string) => {
        const title = value && value.length ? value : placeholder
        return (
            <button className={`h-10 flex w-full px-4 py-2 w-full flex-row items-center justify-between rounded bg-gray-6 leading-7 text-sm ${value && value.length ? "text-gray-1" : "text-gray-3"}`} type={"button"} onClick={() => {
                setCodeType(type)
            }} >
                <p className={"text-left w-full line-clamp-1"}>{title}</p>
                <svg width="13" height="6" viewBox="0 0 13 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 2.25L6.51841 3.99079L3.03683 2.25" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        )
    }

    const dropdownForCodeSelectedInclude = (value: boolean, onChange: (value: boolean) => void) => {
        const data: Array<{name: string, id: boolean}> = [{name: "Include", id: true}, {name: "Exclude", id: false}]
        const title = value ? "Include" : "Exclude"
        return (
            <Listbox value={value} onChange={(isInclude) => {
                onChange(isInclude)
            }}>
                {({open}) => {
                    return (
                        <div className="w-full relative">
                            <Listbox.Button type={"button"} className={`w-full flex flex-none flex-row items-center justify-between px-4 py-1 rounded bg-gray-6 h-10 focus:outline-none ${open ? "border border-theme-blue outline-none ring-4 ring-theme-blue-30" : ""}`}>
                                <p className={"text-sm text-gray-3 leading-7"}>{title}</p>
                                <svg width="13" height="6" viewBox="0 0 13 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 2.25L6.51841 3.99079L3.03683 2.25" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Listbox.Button>
                            {data.length ? (
                                <Transition
                                    as={Fragment}
                                    enter="transition duration-100 ease-out"
                                    enterFrom="transform scale-95 opacity-0"
                                    enterTo="transform scale-100 opacity-100"
                                    leave="transition duration-75 ease-out"
                                    leaveFrom="transform scale-100 opacity-100"
                                    leaveTo="transform scale-95 opacity-0"
                                >
                                    <Listbox.Options static className="z-30 bg-white absolute w-full py-1 mt-1.5 overflow-auto text-base bg-white rounded-lg shadow-2xl border focus:outline-none">
                                        {data.map(({name, id}, idx) => {
                                            return (
                                                <Listbox.Option
                                                    key={idx}
                                                    value={id}
                                                    className={"px-1"}
                                                >
                                                    {({active }) => {
                                                        return (
                                                            <div
                                                                className={`${
                                                                    active ? "bg-theme-blue-10 text-theme-blue font-semibold" : "bg-white text-gray-1 font-normal"
                                                                } w-full py-2 px-4 text-sm rounded`}
                                                            >
                                                                {/* {selected && <i className="fas fa-check mr-4"></i>}*/}
                                                                {name}
                                                            </div>
                                                        )
                                                    }}
                                                </Listbox.Option>
                                            )
                                        })}
                                    </Listbox.Options>
                                </Transition>
                            ) : null}
                        </div>
                    )
                }}
            </Listbox>
        )
    }

    const dataSelectedForIcd = isIncludeIcd ? newGroupSetting.icd : newGroupSetting.excludeIcd
    const dataSelectedForCpt = isIncludeCpt ? newGroupSetting.cpt : newGroupSetting.excludeCpt

    const valueForSelectIcdCodes = dataSelectedForIcd.length ? dataSelectedForIcd.map(({code}) => code).join(", ") : ""
    const valueForSelectCptCodes = dataSelectedForCpt.length ? dataSelectedForCpt.map(({code}) => code).join(", ") : ""
    const insuranceOptions = insuranceList.map(({id, name}) => {
        return {
            id: `${id}:${name}`,
            name: name,
        }
    })
    const $advanceList = isShowAdvanced ? (
        <div className={"w-full grid grid-cols-1 lg:grid-cols-2 gap-x-0 lg:gap-x-4 gap-y-4 mt-4"}>
            <DropdownListForm title={"Plan Type"} id={newGroupSetting.planType} data={dataForPlanType} onChange={onChangePlanType} />
            <DropdownListForm title={"Insurance"} id={`${newGroupSetting.insurance}:${newGroupSetting.insuranceName}`} data={insuranceOptions} onChange={onChangeInsurance} />

            <div>
                <p className={"relative leading-7 text-gray-1 text-sm text-left"}>ICD Code</p>
                <div className={"flex flex-row"}>
                    <div className={"w-32 mr-4 flex-none"}>
                        {dropdownForCodeSelectedInclude(isIncludeIcd, (value) => {
                            setIsIncludeIcd(value)
                        })}
                    </div >
                    <div className={"flex-1"}>
                        {$codeSelectorButton(CodeSelectorType.icd, "Select ICD Code", valueForSelectIcdCodes)}
                    </div>
                </div>
            </div>

            <div>
                <p className={"relative text-gray-1 text-sm text-left leading-7"}>ICD Period</p>
                <div className={"flex flex-row items-center justify-center space-x-2"}>
                    <div className={"flex-1"}>
                        <ButtonForm icon={() => $iconCalendar} title={""} value={formatIcdFromDate} placeholder={"From"} onClick={onClickIcdFrom} />
                    </div >
                    <p>-</p>
                    <div className={"flex-1"}>
                        <ButtonForm icon={() => $iconCalendar} title={""} value={formatIcdToDate} placeholder={"To"} onClick={onClickIcdTo} />
                    </div>
                </div>
            </div>

            <div>
                <p className={"relative text-gray-1 text-sm text-left leading-7"}>CPT Code</p>
                <div className={"flex flex-row"}>
                    <div className={"w-32 mr-4 flex-none"}>
                        {dropdownForCodeSelectedInclude(isIncludeCpt, (isInclude) => {
                            setIsIncludeCpt(isInclude)
                        })}
                    </div >
                    <div className={"flex-1"}>
                        {$codeSelectorButton(CodeSelectorType.cpt, "Select CPT Code", valueForSelectCptCodes)}
                    </div>
                </div>
            </div>

            <div>
                <p className={"relative leading-7 text-gray-1 text-sm text-left"}>CPT Period</p>
                <div className={"flex flex-row items-center justify-center space-x-2"}>
                    <div className={"flex-1"}>
                        <ButtonForm icon={() => $iconCalendar} title={""} value={formatCptFromDate} placeholder={"From"} onClick={onClickCptFrom} />
                    </div >
                    <p>-</p>
                    <div className={"flex-1"}>
                        <ButtonForm icon={() => $iconCalendar} title={""} value={formatCptToDate} placeholder={"To"} onClick={onClickCptTo} />
                    </div>
                </div>
            </div>

            <DropdownListForm title={"Patient Panel"} placeholder={"Patient Panel"} id={newGroupSetting.patientPanel} data={dataForPatientPanel} onChange={onChangePatientPanel} />
        </div>
    ) : null

    const $sectionTitle = (title: string, description: string,) => {
        return (<Fragment>
            <p className={"font-bold text-xl"}>{title}</p>
            <p className={"text-base text-gray-1 mt-2"}>{description}</p>
            <div className={"w-full h-px bg-line mt-4"}/>
        </Fragment>)
    }

    const $matchPatientNumber = (
        <p className={"flex text-base "}>Matching
            <button disabled={!matchingCount} onClick={previewPatientList} className={`${matchingCount > 0 && matchingCount <= MaxMatchingPatientCount ? "text-theme-blue" : "text-red-500"} font-semibold mx-2`}>{matchingCount}</button>
            Patients
        </p>
    )
    const $patientFilterTitle = ( <p className={"font-bold text-xl"}>1. Patient Filters</p>)
    const $sectionPatientFilterTitle = (
        <Fragment>
            <div className={"flex flex-row items-center justify-between"}>
                {$patientFilterTitle}
                {$matchPatientNumber}
            </div>
            <p className={"text-base text-gray-1 mt-2"}>Add filters to create your custom patient groups. Leave blank to target all active patients.</p>
            <div className={"w-full h-px bg-line mt-4"}/>
        </Fragment>
    )

    const checkNumber = (numberString: string) => {
        return /^(0|([1-9]\d*))$/.test(numberString)
    }

    const $age = (
        <div>
            <p className={"relative text-gray-1 text-sm text-left"}>Age</p>
            <div className={"flex flex-row"}>
                <div className={"flex-1 h-full"}>
                    <Form errMsg={fromAgeInvalidMsg} placeholder={"From"} value={newGroupSetting.fromAge} onChangeText={onChangeFromAge} title={""}/>
                </div >
                <div className={"min-h-full w-2 mx-2"}>
                    <div className={"h-0.5 w-2 bg-black mt-6"}/>
                </div>
                <div className={"flex-1"}>
                    <Form errMsg={toAgeInvalidMsg} placeholder={"To"} value={newGroupSetting.toAge} onChangeText={onChangeToAge} title={""}/>
                </div>
                <div className={"flex-1 ml-2"}>
                    <DropdownListForm id={newGroupSetting.ageType} data={dataForAgeType} onChange={onChangeAgeType} />
                </div>
            </div>
        </div>
    )

    const $lastDOS = (
        <div>
            <p className={"relative text-gray-1 text-sm text-left"}>Last DOS</p>
            <div className={"flex flex-row items-center justify-center space-x-2"}>
                <div className={"flex-1"}>
                    <ButtonForm icon={() => $iconCalendar} title={""} value={formatDOSFromDate} placeholder={"From"} onClick={onClickFromDOS} />
                </div >
                <p>-</p>
                <div className={"flex-1"}>
                    <ButtonForm icon={() => $iconCalendar} title={""} value={formatDOSToDate} placeholder={"To"} onClick={onClickToDOS} />
                </div>
            </div>
        </div>
    )

    const _dataForChronicCondition = newGroupSetting.chronicCondition.length ? dataForChronicCondition.map((item) => {
        let _item = Object.assign({}, item)
        _item.isSelected = newGroupSetting.chronicCondition.includes(item.id)
        return _item
    }) : dataForChronicCondition

    const $sectionPatientFilter = (
        <div className={"w-full grid grid-cols-1 lg:grid-cols-2 gap-x-0 lg:gap-x-4 gap-y-4 mt-4"}>
            {$age}
            <DropdownListForm title={"Gender"} id={newGroupSetting.gender} data={dataForGender} onChange={onChangeGender} />

            {/*<DropdownListForm title={"Preferred Contact"} id={newGroupSetting.preferredContact} data={dataForPreferredContact} onChange={onChangePreferredContact} />*/}
            <DropdownListForm title={"Preferred Language"} id={newGroupSetting.preferredLanguage} data={dataForPreferredLanguage} onChange={onChangePreferredLanguage} />

            {/*<DropdownListForm title={"Last DOS (Timeframe)"} id={newGroupSetting.lastDOS} data={dataForLastDOS} onChange={onChangeLastDOS} />*/}
            {$lastDOS}

            {/*<DropdownListForm title={"Appointment Type"} id={newGroupSetting.appointmentType} data={appointmentTypes} onChange={onChangeAppointmentType} />*/}

            <MultiSelectForm title={"Chronic Condition"} placeholder={"Chronic Condition"} data={_dataForChronicCondition} onChange={onChangeChronicCondition} />
        </div>
    )

    const $advanceButton = (
        <div className={"mt-6"}>
            <button type={"button"} className={"flex flex-row items-center space-x-4 py-0 text-primary text-primary text-sm font-medium border-b border-transparent hover:border-primary"} onClick={() => {
                setIsAdvanced(!isShowAdvanced)
            }} >
                <p>Advanced</p>
                <div className={`transform transition duration-300 ease-in-out ${isShowAdvanced ? "rotate-180" : ""}`}>
                    <svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_186_1165)">
                            <path d="M0.6113 3.75H5.63669C5.98435 3.75 6.15817 4.16992 5.91208 4.41602L3.40036 6.92969C3.24802 7.08203 2.99997 7.08203 2.84763 6.92969L0.335909 4.41602C0.0898155 4.16992 0.263644 3.75 0.6113 3.75Z" fill="#2F80ED"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_186_1165">
                                <rect width="6.25" height="10" fill="white"/>
                            </clipPath>
                        </defs>
                    </svg>
                </div>
            </button>
        </div>
    )

    const errMsgForGroupNameForm = (!isValid && !groupName.length) ? "Group name is required" : ""
    const $groupForm = (<Form value={groupName} errMsg={errMsgForGroupNameForm} title={"Group Name"} onChangeText={(text) => {
        setGroupName(text)
    }} />)
    const $sectionGroupTitle = ( <div className={"w-full mt-6 mb-4"}>
        {$sectionTitle("2. Name your Patient Group", "")}
    </div>)
    const $sectionGroup = (
        <Fragment>
            {$sectionGroupTitle}
            {$groupForm}
        </Fragment>
    )

    const onSaveGroupSetting = () => {
        if (!groupName) {
            setIsValid(false)
            return
        }
        if (fromAgeInvalidMsg || toAgeInvalidMsg) {
            return;
        }
        if (matchingCount <= 0) {
            alert("No patients matched")
            return;
        }
        if (matchingCount > MaxMatchingPatientCount) {
            setShowMatchingCountExceedAlert(true)
            return;
        }
        const groupSetting: NewGroupSetting = Object.assign({}, newGroupSetting, {groupName: groupName})

        setIsShowSpinner(true)
        savePatientGroup(
            groupSetting,
            provider,
            excludePatients,
            () => {
                setIsShowSpinner(false)
                setTimeout(() => {
                    setShowNotification(true)
                }, 1000)
                GrayLog.event(actionType === ActionType.new ? LogEventName.newGroup : LogEventName.updateGroup, {isSuccessful: true, ...logEmployee})
            }, (error) => {
                setIsShowSpinner(false)
                showError(error)
                const errMsg = `${error.code} - ${error.msg}`
                GrayLog.event(actionType === ActionType.new ? LogEventName.newGroup : LogEventName.updateGroup, {isSuccessful: false, errMsg: errMsg, ...logEmployee})
            })
    }
    const $footerButton = (
        <div className={"mt-6 grid grid-flow-col auto-cols-max gap-x-4 "}>
            <Button onClick={onSaveGroupSetting}>{actionType === ActionType.new ? "Save Group" : "Update Group"}</Button>
            <Button status={ButtonStatus.light} onClick={() => {
                if (sharedContextState.hasUnSavedChanges) {
                    setShowUnSavedChangeAlert(true)
                } else {
                    onClose()
                }
            }}>Cancel</Button>
        </div>
    )

    const $card = (
        <div className={presentationStyle === PresentationStyle.push ? "w-4/5 rounded-md" : ""}>
            <div className={`w-full bg-white rounded-lg ${presentationStyle === PresentationStyle.push ? "shadow-lg mt-10 p-10" : "p-4"}`}>
                {$sectionPatientFilterTitle}
                {$sectionPatientFilter}
                {$advanceButton}
                {$advanceList}
                {$sectionGroup}
                {$footerButton}
            </div>
        </div>
    )

    const getPickerValue = () => {
        let value: Date | null = new Date()
        switch (calendarPickerType) {
        case CalendarPickerType.cptFrom:
            value = newGroupSetting.cptFrom
            break
        case CalendarPickerType.cptTo:
            value = newGroupSetting.cptTo
            break
        case CalendarPickerType.icdFrom:
            value = newGroupSetting.icdFrom
            break
        case CalendarPickerType.icdTo:
            value = newGroupSetting.icdTo
            break
        case CalendarPickerType.dosFrom:
            value = newGroupSetting.dosFrom
            break
        case CalendarPickerType.dosTo:
            value = newGroupSetting.dosTo
            break
        default:break
        }
        return (value ? value : new Date())
    }

    // const maxToDOSDate = (moment()).subtract(1, "day")
    //                                .toDate()

    const getPickerMaxDate = () => {
        let value: Date | null = null;
        switch (calendarPickerType) {
        case CalendarPickerType.cptFrom:
            value = newGroupSetting.cptTo;
            break;
        case CalendarPickerType.icdFrom:
            value = newGroupSetting.icdTo;
            break;
        case CalendarPickerType.dosFrom:
            value = newGroupSetting.dosTo || (new Date());
            break;
        case CalendarPickerType.dosTo:
            value = (new Date());
            break;
        default:break;
        }
        return value || (new Date(3000, 0, 0))
    }

    const getPickerMinDate = () => {
        let value: Date | null = null;
        switch (calendarPickerType) {
        case CalendarPickerType.cptTo:
            value = newGroupSetting.cptFrom;
            break;
        case CalendarPickerType.icdTo:
            value = newGroupSetting.icdFrom;
            break;
        case CalendarPickerType.dosTo:
            value = newGroupSetting.dosFrom;
            break;
        default:break;
        }
        return value || (new Date(1900, 0, 0))
    }

    const $CalendarPicker = (
        <CalendarPicker
            isOpen={showCalendarPicker}
            value={getPickerValue()}
            maxDate={getPickerMaxDate()}
            minDate={getPickerMinDate()}
            onSelectDate={$onChangeDate}
            onClose={() => {
                setShowCalendar(false)
            }}
        />
    )

    const onClose = () => {
        if (presentationStyle === PresentationStyle.push) {
            history.goBack()
        } else {
            onDismiss && onDismiss()
        }
        sharedContextState.hasUnSavedChanges && dispatchSharedContext({type: SharedContextActionType.unsavedChange, state: false})
    }

    const $spinner = (<Spinner isOpen={isShowSpinner} />)
    const onClickSaveSuccessClose = () => {
        setShowNotification(false)
        onClose()
    }
    const $saveSuccessNotification = (
        <Notification
            isOpen={showNotification}
            type={NotificationType.success}
            title={`Your patient group has been ${newGroupSetting.id.length ? "updated" : "created"}.`}
            onClose={onClickSaveSuccessClose}
        />
    )

    const dataForCodeTemplate = codeType === CodeSelectorType.icd ? dataForIcdCodeTemplate : dataForCptCodeTemplate
    const selectedCodes = useMemo(() => {
        if (codeType === CodeSelectorType.icd) {
            return isIncludeIcd ? newGroupSetting.icd : newGroupSetting.excludeIcd
        } else if (codeType === CodeSelectorType.cpt) {
            return isIncludeCpt ? newGroupSetting.cpt : newGroupSetting.excludeCpt
        }
        return []
    }, [codeType, isIncludeCpt, isIncludeIcd])

    const onChangeCodes = (codes: Array<CodeItem>) => {
        if (codeType === CodeSelectorType.icd) {
            let icdCodes:Array<CodeItem> = []
            let excludeIcdCodes:Array<CodeItem> = []
            if (isIncludeIcd) {
                icdCodes = codes
            } else {
                excludeIcdCodes = codes
            }
            setNewGroupSetting({
                ...newGroupSetting,
                icd: icdCodes,
                excludeIcd: excludeIcdCodes,
            })
        } else if (codeType === CodeSelectorType.cpt) {
            let cptCodes:Array<CodeItem> = []
            let excludeCptCodes:Array<CodeItem> = []
            if (isIncludeCpt) {
                cptCodes = codes
            } else {
                excludeCptCodes = codes
            }
            setNewGroupSetting({
                ...newGroupSetting,
                cpt: cptCodes,
                excludeCpt: excludeCptCodes,
            })
        }
    }

    const $codeSelectorModal = (
        <CustomModal isOpen={showCodeSelector} >
            <CodeSelector dataForTemplate={dataForCodeTemplate} type={codeType} selectedCodes={selectedCodes} onClose={() => {
                setShowCodeSelector(false)
                setCodeType(CodeSelectorType.none)
            }} onConfirm={(codes) => {
                setCodeType(CodeSelectorType.none)
                setShowCodeSelector(false)
                onChangeCodes(codes)
            }}/>
        </CustomModal>
    )

    const $patientPreviewModal = (
        <CustomModal isOpen={showPatientPreviewModal}>
            <PatientPreviewList
                groupSetting={newGroupSetting}
                excludePatients={excludePatients}
                actionType={ActionType.edit}
                onClose={(_excludePatients) => {
                    setShowPatientPreviewModal(false)
                    if (_excludePatients) {
                        setExcludePatients(_excludePatients)
                    }
                }}
            />
        </CustomModal>
    )

    const $unSavedChangeAlert = (
        <Alert
            isOpen={showUnSavedChangeAlert}
            title={"Unsaved Changes"}
            description={"Are you sure you want to discard this draft? Any changes made in the draft will be lost."}
            rejectAction={{title: "Cancel", onClick: () => {
                setShowUnSavedChangeAlert(false)
            }}
            }
            resolveAction={{title: "Discard", onClick: () => {
                setShowUnSavedChangeAlert(false)
                onClose()
            }}
            }
        />
    )

    const $matchingCountExceedAlert = (
        <Alert
            isOpen={showMatchingCountExceedAlert}
            title={"Patient Group Limit Reached"}
            description={`Patient number for each patient cannot exceed ${MaxMatchingPatientCount}. (Limit to be lifted soon)`}
            resolveAction={{title: "Got it", onClick: () => {
                setShowMatchingCountExceedAlert(false)
            }}
            }
        />
    )

    return (
        <div className={`w-full flex flex-col items-center ${presentationStyle === PresentationStyle.push ? "mt-6 bg-gray-6 px-10" : ""}`}>
            {$header}
            {$card}
            {$CalendarPicker}
            {$spinner}
            {$saveSuccessNotification}
            {$codeSelectorModal}
            {$patientPreviewModal}
            {$unSavedChangeAlert}
            {$matchingCountExceedAlert}
        </div>
    )
}

export default NewGroup
